// Maps a legacy (v1) body-measurement record to the rich v2 shape. The old schema
// had five single-value fields; the new one is per-bodypart with relax/flex and L/R.
// Best-effort: the legacy single values land on the closest new field, the rest are
// null. Pure so it can be unit-tested and reused by the Dexie upgrade.

import type { BodyMeasurement } from '../domain/types';

interface LegacyMeasurement {
  id: string;
  date: string;
  bodyweightKg?: number | null;
  waistCm?: number | null;
  armCm?: number | null;
  chestCm?: number | null;
  thighCm?: number | null;
  note?: string;
}

function emptyMeasurement(id: string, date: string): BodyMeasurement {
  return {
    id,
    date,
    bodyweightKg: null,
    waistCm: null,
    bellyCm: null,
    chestRelaxCm: null,
    chestFlexCm: null,
    shouldersCm: null,
    neckCm: null,
    armRelaxLCm: null,
    armRelaxRCm: null,
    armFlexLCm: null,
    armFlexRCm: null,
    thighRelaxLCm: null,
    thighRelaxRCm: null,
    thighFlexLCm: null,
    thighFlexRCm: null,
    calfLCm: null,
    calfRCm: null,
    wristLCm: null,
    wristRCm: null,
    forearmFlexLCm: null,
    forearmFlexRCm: null,
    note: '',
  };
}

// Already-v2 records pass through untouched (idempotent); legacy fields are folded
// onto the nearest new column.
export function migrateLegacyMeasurement(raw: Record<string, unknown>): BodyMeasurement {
  const legacy = raw as unknown as LegacyMeasurement;
  const out = emptyMeasurement(legacy.id, legacy.date);

  // Carry over any field that already matches the new shape (re-running migration,
  // or a record that was partially v2).
  for (const key of Object.keys(out) as (keyof BodyMeasurement)[]) {
    if (key in raw && raw[key] != null) {
      (out as unknown as Record<string, unknown>)[key] = raw[key];
    }
  }

  // Legacy-only fields -> closest new column (only when the new column is still empty).
  if (out.bodyweightKg == null && legacy.bodyweightKg != null) out.bodyweightKg = legacy.bodyweightKg;
  if (out.waistCm == null && legacy.waistCm != null) out.waistCm = legacy.waistCm;
  if (out.chestRelaxCm == null && legacy.chestCm != null) out.chestRelaxCm = legacy.chestCm;
  if (out.thighRelaxLCm == null && legacy.thighCm != null) out.thighRelaxLCm = legacy.thighCm;
  if (out.armFlexLCm == null && legacy.armCm != null) out.armFlexLCm = legacy.armCm;
  if (legacy.note != null) out.note = legacy.note;

  return out;
}
